import React, { useState } from "react";
import { Mail, MapPin, Clock, Send, CheckCircle2, MessageCircle } from "lucide-react";
import client from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const Contact = () => {
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await client.post("/contact", f); setSent(true); toast.success("Message sent — we'll be in touch."); }
    catch { toast.error("Failed to send. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white"><Navbar />
      <section className="bg-graphite text-white py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="mono text-xs uppercase tracking-[0.2em] text-orange mb-3">Contact Us</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">Let's get your equipment back up.</h1>
          <p className="text-white/70 mt-4 max-w-2xl text-lg">Questions about a part, an order or a quote? Send us a message and our team will get back to you.</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14 grid lg:grid-cols-3 gap-8">
        <div className="space-y-5">
          {[[Mail, "Email", "support@gotparts.ca"], [MapPin, "Location", "Canada"], [Clock, "Hours", "Mon–Fri, 9:00–17:00"], [MessageCircle, "Live Chat", "Use the chat bubble, bottom-right"]].map(([Icon, t, v]) => (
            <div key={t} className="flex items-start gap-3"><div className="w-10 h-10 rounded-md bg-orange/10 grid place-items-center shrink-0"><Icon className="w-5 h-5 text-orange" /></div><div><div className="font-semibold text-graphite">{t}</div><div className="text-sm text-steel-light">{v}</div></div></div>
          ))}
        </div>

        <div className="lg:col-span-2">
          {sent ? (
            <div className="border border-border rounded-lg p-10 text-center" data-testid="contact-success">
              <CheckCircle2 className="w-12 h-12 text-orange mx-auto mb-3" />
              <h3 className="text-xl font-bold text-graphite">Thank you!</h3>
              <p className="text-steel-light mt-1">Your message has been received. We'll respond as soon as possible.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="border border-border rounded-lg p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Name</Label><Input data-testid="contact-name" value={f.name} onChange={set("name")} className="mt-1.5 h-11" required /></div>
                <div><Label>Email</Label><Input data-testid="contact-email" type="email" value={f.email} onChange={set("email")} className="mt-1.5 h-11" required /></div>
              </div>
              <div><Label>Subject</Label><Input data-testid="contact-subject" value={f.subject} onChange={set("subject")} className="mt-1.5 h-11" /></div>
              <div><Label>Message</Label><Textarea data-testid="contact-message" value={f.message} onChange={set("message")} rows={5} className="mt-1.5" required /></div>
              <Button type="submit" data-testid="contact-submit" disabled={loading} className="bg-orange hover:bg-orange-hover h-11"><Send className="w-4 h-4 mr-2" /> {loading ? "Sending…" : "Send Message"}</Button>
            </form>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Contact;
